import * as React from 'react';

import './RequestStep.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { IconButton } from '../design-system/components';

import {
    FieldGuideMaxFileNameLength,
    stripControlCharacters,
    validateFieldGuideFileName,
} from '../../Utils/FieldGuideFileName';

type WorkbookNameFieldLabels = {
    rename: (fileName: string) => string;
    reset: string;
    limitReached: string;
    required: string;
};

type WorkbookNameFieldProps = {
    // The name the workbook has before any rename; renaming back to it needs no reset.
    originalName: string;
    name: string;
    labels: WorkbookNameFieldLabels;
    onCommit: (name: string) => void;
    // Reports whether the field is holding a name that cannot be committed, so the step can hold back Review.
    onInvalidChange: (invalid: boolean) => void;
};

type NameError = 'empty' | 'tooLong';

/**
 * A workbook's file name: plain text that reveals a rename button on hover or focus, swaps to an input while
 * editing, and offers a reset once the name differs from the original. Enter or blur commits, Escape cancels.
 */
export const WorkbookNameField: React.FC<WorkbookNameFieldProps> = (props): React.ReactElement => {
    const { originalName, name, labels, onCommit, onInvalidChange } = props;

    const [editing, setEditing] = React.useState<boolean>(false);
    const [draft, setDraft] = React.useState<string>(name);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const messageIdPrefix = React.useId();
    // Set once Enter or Escape has finished the edit, so the blur that follows does not commit it a second time.
    const finished = React.useRef<boolean>(false);

    // Set when a commit is refused because the name with its extension is over the limit; typing clears it.
    const [tooLong, setTooLong] = React.useState<boolean>(false);
    // An empty draft is judged on every keystroke, so its message and the held-back Review show before any commit.
    const draftResult = editing ? validateFieldGuideFileName(draft) : undefined;
    const error: NameError | undefined = draftResult?.valid === false && draftResult.reason !== 'tooLong' ? 'empty' : tooLong ? 'tooLong' : undefined;
    const invalid = editing && error !== undefined;
    const onInvalidChangeRef = React.useRef(onInvalidChange);
    onInvalidChangeRef.current = onInvalidChange;
    React.useEffect(() => {
        onInvalidChangeRef.current(invalid);
        return () => onInvalidChangeRef.current(false);
    }, [invalid]);

    React.useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    const startEditing = (): void => {
        finished.current = false;
        setDraft(name);
        setTooLong(false);
        setEditing(true);
    };

    const stopEditing = (): void => {
        finished.current = true;
        setEditing(false);
    };

    const commit = (): void => {
        // The draft has its control characters stripped as it is typed, so an invalid draft is empty or too long.
        const result = validateFieldGuideFileName(draft);
        if (result.valid === false) {
            setTooLong(result.reason === 'tooLong');
            return;
        }

        stopEditing();
        onCommit(result.name);
    };

    const atLimit = draft.length >= FieldGuideMaxFileNameLength || error === 'tooLong';
    const requiredId = `${messageIdPrefix}-required`;
    const limitId = `${messageIdPrefix}-limit`;
    const describedBy = [error === 'empty' ? requiredId : undefined, atLimit ? limitId : undefined].filter(Boolean).join(' ') || undefined;

    if (editing) {
        return (
            <div className={'page-mapping-row__source-text'}>
                <input
                    ref={inputRef}
                    className={'page-rename-text page-mapping-row__source-name page-rename-text--editing'}
                    value={draft}
                    maxLength={FieldGuideMaxFileNameLength}
                    aria-label={labels.rename(name)}
                    aria-invalid={error !== undefined}
                    aria-describedby={describedBy}
                    onChange={event => {
                        setDraft(stripControlCharacters(event.target.value));
                        setTooLong(false);
                    }}
                    onBlur={() => { if (!finished.current) { commit(); } }}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            commit();
                        } else if (event.key === 'Escape') {
                            stopEditing();
                        }
                    }}
                />
                {error === 'empty' && <span id={requiredId} role={'alert'} className={'page-help-text page-help-text--error'}>{labels.required}</span>}
                {atLimit && <span id={limitId} role={'alert'} className={'page-help-text page-help-text--error'}>{labels.limitReached}</span>}
            </div>
        );
    }

    return (
        <div className={'page-mapping-row__source-text'}>
            <span className={'page-rename-field'}>
                <span className={'page-mapping-row__source-name'} title={name}>{name}</span>
                <IconButton
                    type={'floating'}
                    size={'small'}
                    className={'page-rename-field__button'}
                    label={labels.rename(name)}
                    icon={<FontAwesomeIcon icon={faPen} />}
                    onClick={startEditing}
                />
            </span>
            {name !== originalName && (
                <span className={'page-help-text'}>
                    <button type={'button'} className={'page-text-link'} onClick={() => onCommit(originalName)}>{labels.reset}</button>
                </span>
            )}
        </div>
    );
};
